import { useState } from 'react';
import type { AdminTabId } from '../types';

interface NavItem {
  id: AdminTabId;
  icon: string;
  label: string;
  count?: number;
}

interface ActivityItem {
  id: number;
  type: 'client' | 'invoice' | 'message' | 'project' | 'task' | 'payment';
  text: string;
  time: string;
  unread?: boolean;
}

const navItems: NavItem[] = [
  { id: 'dashboard', icon: 'ri-dashboard-3-line', label: 'Dashboard' },
  { id: 'klienti',   icon: 'ri-user-3-line',      label: 'Klienti',   count: 12 },
  { id: 'projekty',  icon: 'ri-folder-3-line',    label: 'Projekty',  count: 8  },
  { id: 'faktury',   icon: 'ri-bill-line',         label: 'Faktúry',   count: 3  },
  { id: 'spravy',    icon: 'ri-message-3-line',    label: 'Správy',    count: 4  },
  { id: 'ulohy',     icon: 'ri-task-line',         label: 'Úlohy',     count: 14 },
];

const recentActivity: ActivityItem[] = [
  { id: 1, type: 'message',  text: 'Petra K.: Kedy bude report?',         time: 'pred 5 min',   unread: true  },
  { id: 2, type: 'payment',  text: 'Platba BOO-2025-019 prijatá',         time: 'pred 32 min',  unread: true  },
  { id: 3, type: 'client',   text: 'Nový klient: TechFlow s.r.o.',        time: 'pred 2 hod',   unread: true  },
  { id: 4, type: 'project',  text: 'Fáza Vývoj: Pet Shop dokončená',      time: 'pred 3 hod'                 },
  { id: 5, type: 'task',     text: 'Úloha SEO audit pridelená Marekovi',  time: 'pred 5 hod'                 },
  { id: 6, type: 'invoice',  text: 'Faktúra BOO-2025-021 vystavená',      time: 'pred 1 dňom'                },
];

const activityMeta: Record<ActivityItem['type'], { icon: string; color: string; bg: string }> = {
  client:   { icon: 'ri-user-add-line',          color: 'text-emerald-400',  bg: 'bg-emerald-500/10 border-emerald-500/20'  },
  invoice:  { icon: 'ri-bill-line',              color: 'text-amber-400',    bg: 'bg-amber-500/10 border-amber-500/20'      },
  message:  { icon: 'ri-message-3-line',         color: 'text-orange-400',   bg: 'bg-orange-500/10 border-orange-500/20'   },
  project:  { icon: 'ri-folder-check-line',      color: 'text-violet-400',   bg: 'bg-violet-500/10 border-violet-500/20'   },
  task:     { icon: 'ri-checkbox-circle-line',   color: 'text-sky-400',      bg: 'bg-sky-500/10 border-sky-500/20'         },
  payment:  { icon: 'ri-secure-payment-line',    color: 'text-teal-400',     bg: 'bg-teal-500/10 border-teal-500/20'       },
};

interface AdminSidebarProps {
  activeTab: AdminTabId;
  onTabChange: (tab: AdminTabId) => void;
}

export default function AdminSidebar({ activeTab, onTabChange }: AdminSidebarProps) {
  const [feedOpen, setFeedOpen] = useState(true);
  const [seenAll, setSeenAll] = useState(false);

  const unreadCount = seenAll ? 0 : recentActivity.filter((a) => a.unread).length;

  const handleFeedToggle = () => {
    setFeedOpen((v) => {
      if (!v) setSeenAll(true);
      return !v;
    });
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-zinc-900 border-r border-zinc-800 flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <i className="ri-shield-star-fill text-white text-sm" />
        </div>
        <div>
          <div className="text-white font-black text-base leading-none">
            boost<span className="text-orange-500">io</span>
          </div>
          <div className="text-zinc-500 text-[10px] mt-0.5 font-medium tracking-wider uppercase">Admin</div>
        </div>
      </div>

      {/* Admin badge */}
      <div className="px-5 py-3.5 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">MB</span>
          </div>
          <div className="min-w-0">
            <p className="text-white text-xs font-semibold truncate">Martin Boostio</p>
            <div className="flex items-center gap-1 mt-0.5">
              <i className="ri-shield-star-line text-orange-400 text-[10px]" />
              <p className="text-orange-400 text-[10px] font-semibold">Super Admin</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable area */}
      <div className="flex-1 overflow-y-auto">
        {/* Navigation */}
        <nav className="p-3 space-y-0.5">
          <p className="text-zinc-600 text-[9px] font-bold uppercase tracking-widest px-3 pb-2 pt-1">Navigácia</p>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-left transition-all duration-150 whitespace-nowrap ${
                activeTab === item.id
                  ? 'bg-orange-500/10 border border-orange-500/20'
                  : 'hover:bg-zinc-800/60 border border-transparent'
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${activeTab === item.id ? 'text-orange-400' : 'text-zinc-500'}`}>
                <i className={`${item.icon} text-base`} />
              </div>
              <span className={`text-sm font-medium flex-1 ${activeTab === item.id ? 'text-orange-300' : 'text-zinc-400'}`}>
                {item.label}
              </span>
              {item.count != null && (
                <span className={`text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${activeTab === item.id ? 'bg-orange-500 text-white' : 'bg-zinc-700 text-zinc-400'}`}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="mx-3 border-t border-zinc-800" />

        {/* Activity feed */}
        <div className="p-3">
          <button
            onClick={handleFeedToggle}
            className="w-full flex items-center gap-2 px-3 pb-2 pt-1 cursor-pointer group"
          >
            <p className="text-zinc-600 text-[9px] font-bold uppercase tracking-widest flex-1 text-left group-hover:text-zinc-400 transition-colors">
              Live aktivita
            </p>
            {unreadCount > 0 && !feedOpen && (
              <span className="bg-orange-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0">
                {unreadCount}
              </span>
            )}
            <i className={`ri-arrow-${feedOpen ? 'up' : 'down'}-s-line text-zinc-600 text-sm group-hover:text-zinc-400 transition-all flex-shrink-0`} />
          </button>

          {feedOpen && (
            <div className="space-y-0.5">
              {recentActivity.map((item, idx) => {
                const meta = activityMeta[item.type];
                const isUnread = item.unread && !seenAll;
                return (
                  <div
                    key={item.id}
                    className={`relative flex items-start gap-2.5 px-2 py-2 rounded-xl transition-all ${isUnread ? 'bg-zinc-800/50' : 'hover:bg-zinc-800/30'}`}
                  >
                    {idx < recentActivity.length - 1 && (
                      <div className="absolute left-[18px] top-[28px] bottom-[-6px] w-px bg-zinc-800" />
                    )}
                    <div className={`w-6 h-6 rounded-lg border flex items-center justify-center flex-shrink-0 z-10 ${meta.bg}`}>
                      <i className={`${meta.icon} ${meta.color} text-[10px]`} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className={`text-[11px] leading-snug truncate ${isUnread ? 'text-zinc-200 font-medium' : 'text-zinc-500'}`}>
                        {item.text}
                      </p>
                      <p className="text-zinc-700 text-[10px] mt-0.5">{item.time}</p>
                    </div>
                    {isUnread && (
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom */}
      <div className="p-3 border-t border-zinc-800 space-y-0.5 flex-shrink-0">
        <a href="/portal" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800/60 border border-transparent cursor-pointer transition-all whitespace-nowrap">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-external-link-line text-zinc-600 text-base" />
          </div>
          <span className="text-zinc-600 text-sm font-medium">Klientský portál</span>
        </a>
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800/60 border border-transparent cursor-pointer transition-all whitespace-nowrap">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-settings-3-line text-zinc-600 text-base" />
          </div>
          <span className="text-zinc-600 text-sm font-medium">Nastavenia</span>
        </button>
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800/60 border border-transparent cursor-pointer transition-all whitespace-nowrap">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-line text-zinc-600 text-base" />
          </div>
          <span className="text-zinc-600 text-sm font-medium">Odhlásiť sa</span>
        </button>
      </div>
    </aside>
  );
}
